import { useEffect, useState } from 'react';

/**
 * Renders a backend-served image using a credentialed fetch so that
 * auth cookies are included in the request.
 *
 * - Relative ("/...") and blob/data URIs are used as-is (no fetch needed).
 * - Absolute HTTP(S) URLs are fetched with `credentials: 'include'` and
 *   displayed via a temporary object URL (revoked on unmount / src change).
 * - `fallbackSrc` is shown while loading or when the fetch fails.
 */
const AuthenticatedImage = ({ src, fallbackSrc, alt, ...rest }) => {
    const [imgSrc, setImgSrc] = useState(null);

    useEffect(() => {
        if (!src) {
            setImgSrc(null);
            return;
        }

        // Local / static URLs — no credentialed fetch needed
        if (src.startsWith('/') || src.startsWith('blob:') || src.startsWith('data:')) {
            setImgSrc(src);
            return;
        }

        // Absolute URL — fetch with auth cookies
        let objectUrl = null;
        let cancelled = false;

        fetch(src, { credentials: 'include' })
            .then(res => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                return res.blob();
            })
            .then(blob => {
                if (!cancelled) {
                    objectUrl = URL.createObjectURL(blob);
                    setImgSrc(objectUrl);
                }
            })
            .catch(() => {
                if (!cancelled) setImgSrc(null);
            });

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [src]);

    return <img src={imgSrc || fallbackSrc} alt={alt} {...rest} />;
};

export default AuthenticatedImage;
